import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatTable } from '@angular/material/table';
import { Catch } from '../shared/catch.model';
import { FishingEvent } from '../shared/fishing-event.model';
import { TrawlSet } from '../shared/trawl-set.model';
import { GroundfishDelivery } from '../shared/groundfish-delivery.model';
import { HMSDelivery } from '../shared/hms-delivery.model';
import { TrawlDelivery } from '../shared/trawl-delivery.model';
import { LocalStorageService } from '../shared/local-storage.service';
import { OperatorUser } from '../shared/operator-user.model';


/**
 * A “year‑to‑date” (or arbitrary range) roll‑up of ALL catch:
 * ▸ sets still on the device  (pending trip)
 * ▸ sets already submitted, inside completed deliveries
 * Sets on drafts are not included.
 * The default range is Jan 1 → today; users can pick any dates.
 *
 */
@Component({
    selector: 'app-summary-report-ytd',
    templateUrl: './summary-report-ytd.component.html',
    styleUrls: ['./summary-report-ytd.component.css'],
    standalone: false
})
export class SummaryReportYtdComponent implements OnInit  {

  // template binds
  catchItems: Catch[] = []; // used for single fishery
  catchGFItems: Catch[] = []; //used for combo fishery
  catchHMSItems: Catch[] = [];
  displayedColumns = ['Species','RetainedCount','RetainedWeight','ReleasedCount','ReleasedWeight'];
  title = 'All Sets (YTD)';
  fishery!: 'Groundfish' | 'HMS' | 'Groundfish + HMS' | 'Trawl';
  user: OperatorUser = LocalStorageService.current_user;
  completedHMS: HMSDelivery[] = LocalStorageService.received_logbooks.filter((lb): lb is HMSDelivery => lb instanceof HMSDelivery);
  completedGF: GroundfishDelivery[] = LocalStorageService.received_logbooks.filter((lb): lb is GroundfishDelivery => lb instanceof GroundfishDelivery);
  completedTrawl: TrawlDelivery[] = LocalStorageService.received_trawlLogbooks;;
  HMSDaysFished: number = 0;
  showCalendars = false;  

  dateRange = {
    start: new Date(new Date().getFullYear(), 0, 1),
    end  : new Date()
  };

  @ViewChild(MatTable) table!: MatTable<Catch>;

  constructor(private route: ActivatedRoute, private router: Router) {}

  ngOnInit(): void {
    this.fishery = this.user.trawlFishery ? 'Trawl' : this.user.hmsFishery && this.user.groundfishFishery ? 'Groundfish + HMS' : this.user.hmsFishery ? 'HMS' : 'Groundfish';
    this.refreshTable();
  }

  /** Re‑build the table any time the range changes */
  refreshTable(): void {
    const start = this.dateRange.start, end = this.dateRange.end;
    this.title = `All ${this.fishery} Catch`;
    if (this.fishery === 'Groundfish + HMS') {
      // build each one separately
      this.catchGFItems = this.buildAggregate('Groundfish', start, end);
      this.catchHMSItems = this.buildAggregate('HMS', start, end);
    } else {
      // single‑fishery path
      this.catchItems = this.buildAggregate(this.fishery as 'Groundfish' | 'HMS' | 'Trawl', start, end);
    }
    
    // Calculate days fished & completed trips for HMS
    if (this.fishery === 'HMS' || this.fishery === 'Groundfish + HMS') {
      const fishSets = this.collectRelevantSetsFor('HMS').filter((s): s is FishingEvent => 'fishingMode' in s && s.fishingMode === 'Fish' && new Date(s.startDate) >= start && new Date(s.startDate) <= end);
      this.HMSDaysFished = fishSets.length;
      this.completedHMS = LocalStorageService.received_logbooks.filter((lb): lb is HMSDelivery => lb instanceof HMSDelivery &&  lb.arrivalDate >= start && lb.arrivalDate <= end);

    }
    // Calculate completed trips for Groundfish/Trawl
    if (this.fishery === 'Groundfish' || this.fishery === 'Groundfish + HMS') {
      this.completedGF = LocalStorageService.received_logbooks.filter((lb): lb is GroundfishDelivery => lb instanceof GroundfishDelivery && lb.returnDate >= start && lb.returnDate <= end);
    } else if (this.fishery === 'Trawl') {
      this.completedTrawl = LocalStorageService.received_trawlLogbooks.filter((lb): lb is TrawlDelivery => lb instanceof TrawlDelivery && lb.returnDate >= start && lb.returnDate <= end);
    }
    // force table redraw
    if (this.table) this.table.renderRows();
}


  private buildAggregate(
    fishery: 'Groundfish'|'HMS' |'Trawl',
    start: Date, end: Date
  ): Catch[]
  {
  // 1) collect & date‑filter
  let allSets = this.collectRelevantSetsFor(fishery);
  if (fishery == 'Groundfish') 
    allSets = allSets.filter(s => !!s.endDate && new Date(s.endDate!) >= start && new Date(s.endDate!) <= end);
  else if (fishery == 'HMS')
    allSets = allSets.filter(s => new Date(s.startDate) >= start && new Date(s.startDate) <= end);

  // 2) aggregate by species
  const bySpecies = new Map<string,Catch>();
  allSets.forEach(set => set.catchItems.forEach(ci => {
    const k = ci.species.name;
    if (!bySpecies.has(k)) {
      bySpecies.set(k, Catch.transformAnonymous([ci])[0]);
      return;
    }
    const agg = bySpecies.get(k)!;
    agg.retainedCount  = (agg.retainedCount  ?? 0) + (ci.retainedCount  ?? 0);
    agg.releasedCount  = (agg.releasedCount  ?? 0) + (ci.releasedCount  ?? 0);
    agg.retainedWeight = (agg.retainedWeight ?? 0) + (ci.retainedWeight ?? 0);
    agg.releasedWeight = (agg.releasedWeight ?? 0) + (ci.releasedWeight ?? 0);
  }));

  return [...bySpecies.values()];
  }

  /** Gathers:
   *   ▸ current‑trip sets
   *   ▸ sets in any completed deliveries (received_logbooks / received_trawlLogbooks)
   *   ▸ sets in any drafts (hms_drafts / trawl_drafts)
   */
private collectRelevantSetsFor(fishery: 'Groundfish' | 'HMS' | 'Trawl'): (FishingEvent | TrawlSet)[] {
  if (fishery === 'Trawl') {
    const pending: TrawlSet[] = LocalStorageService.user_trawlTrip.sets;
    const completed: TrawlSet[] = LocalStorageService.received_trawlLogbooks.map((d: TrawlDelivery) => d.sets).reduce((all, arr) => all.concat(arr), []);
    const drafts: TrawlSet[] = LocalStorageService.trawl_drafts.map((d: TrawlDelivery) => d.sets).reduce((all, arr) => all.concat(arr), []);
    return [...pending, ...completed, ...drafts];
  }

  // pending 
  const pending = LocalStorageService.user_trip.sets.filter(s => s.fishery === fishery);

  // completed
  const completed = LocalStorageService.received_logbooks.filter((lb) => lb instanceof (fishery === 'Groundfish' ? GroundfishDelivery : HMSDelivery))
      .reduce((all, arr) => all.concat(arr.sets), [] as FishingEvent[]);

  // drafts (HMS only)
  const drafts = fishery === 'HMS' ?
    LocalStorageService.hms_drafts.reduce((all, arr) => all.concat(arr.sets), [] as FishingEvent[])
    : [];

  return [...pending, ...completed, ...drafts];
}

  formatRange(s: Date, e: Date): string {
    const opts: Intl.DateTimeFormatOptions = {
      month: 'short',
      day:   'numeric',
      year:  'numeric'
    };
    const fmt = (d: Date) => d.toLocaleDateString(undefined, opts);
    return `${fmt(s)} - ${fmt(e)}`;
  }

  /** Called when the user picks a new start date */
  onStartDate(date: Date) {
    this.dateRange.start = date;
    this.refreshTable();
  }
  
  /** Called when the user picks a new end date */
  onEndDate(date: Date) {
    this.dateRange.end = date;
    this.refreshTable();
  }

  navigateBack(): void {
    this.router.navigate(['menu']);
  }
}
